import API from "../server/serverInterface"
import { navigate } from "gatsby"
import { useAuthState, AuthStateType } from "../contexts/authContext"
import {
  ApolloError,
  useMutation,
  MutationFunction,
  PureQueryOptions,
  RefetchQueriesFunction,
} from "@apollo/client"
import { CheckErrors } from "../utils/grapqhlErrorCheck"
import { useEffect, useState } from "react"

interface hookObjectRes {
  loading: boolean
  error?: ApolloError
  errMsg?: string
  data?: any
  loadingAuth: boolean
}
declare type QueryTuple = [MutationFunction, hookObjectRes]

export const useApiMutation = (
  clientName: string,
  apiDef: string,
  refetchQueries?:
    | (string | PureQueryOptions)[]
    | RefetchQueriesFunction
    | undefined,
  includeAuthEmailAs?: string
): QueryTuple => {
  const authState = useAuthState() as AuthStateType
  const [loadingAuth, setLoadingAuth] = useState(true)

  const [errMsg, setErrorMsg] = useState("")
  const [err, setError] = useState(undefined)
  const [preAuthCalled, setPreAuthCalled] = useState(false)
  const [exParams, setParams] = useState<Record<string, any> | undefined>(
    undefined
  )
  const [exFn, setExecutionFn] = useState({
    fn: async (params: Record<string, any>): Promise<void> => {
      // this needs to auto call once we are ready
      setParams(params)
      setPreAuthCalled(true)
    },
  })

  const client = API.getClientDef(clientName)
  if (!client) {
    throw Error("Client not found")
  }

  const queryDef = client.getDef(apiDef)
  if (!queryDef) {
    throw Error("Api not found")
  }
  if (queryDef.type !== "mutation") {
    throw Error("Api is not a mutation")
  }

  const [executeMutation, queryResult] = useMutation(queryDef.gql, {
    client: client.client,
    refetchQueries,
  })

  const runQuery = (params?: Record<string, any>) => {
    if (
      includeAuthEmailAs &&
      authState.isLoggedIn() &&
      params &&
      params.variables
    ) {
      params.variables[includeAuthEmailAs] = authState.email?.toLowerCase()
    }
    try {
      executeMutation(params)
    } catch (error) {
      setErrorMsg("There was a problem executing your request")
      setError(error)
    }
  }

  useEffect(() => {
    if (setErrorMsg) {
      setErrorMsg(CheckErrors(queryResult?.error, queryDef.authRequired) || "")
    }
  }, [queryResult])

  useEffect(() => {
    if (
      !queryDef.authRequired ||
      (authState.localStorageLoaded && authState.isLoggedIn())
    ) {
      if (preAuthCalled) {
        // don't want to do this again
        setPreAuthCalled(false)
        runQuery(exParams)
      }

      setExecutionFn({
        fn: async (params: any): Promise<void> => {
          runQuery(params)
        },
      })

      setLoadingAuth(false)
    }
  }, [authState, exParams, preAuthCalled])

  return [
    exFn.fn,
    {
      ...queryResult,
      loading: loadingAuth || queryResult.loading,
      error: queryResult.error || err,
      errMsg,
      loadingAuth,
    },
  ]
}
